<!-- =========================================================================================
  File Name: TheNavbar.vue
  Description: Navbar component
  Component Name: TheNavbar

========================================================================================== -->

<template>
	<div class="relative">
		<div class="vx-navbar-wrapper navbar-full p-0">
			<vs-navbar class="navbar-custom navbar-skelton" :class="navbarClasses" :style="navbarStyle" :color="navbarColor">
				<template v-if="windowWidth >= 992">
					<!-- STARRED PAGES - FIRST 10 -->
					<ul class="vx-navbar__starred-pages">
						<draggable v-model="starredPagesLimited" :group="{ name: 'pinList' }" class="flex cursor-move">
							<li class="starred-page" v-for="page in starredPagesLimited" :key="page.url">
								<vx-tooltip :text="page.label" position="bottom" delay=".3s">
									<feather-icon svgClasses="h-6 w-6" class="p-2 cursor-pointer" :icon="page.labelIcon" @click="$router.push(page.url).catch(() => {})" />
								</vx-tooltip>
							</li>
						</draggable>
					</ul>

					<!-- STARRED PAGES MORE -->
					<div class="vx-navbar__starred-pages--more-dropdown" v-if="starredPagesMore.length">
						<vs-dropdown vs-custom-content vs-trigger-click>
							<feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" class="cursor-pointer p-2"></feather-icon>
							<vs-dropdown-menu>
								<ul class="vx-navbar__starred-pages-more--list">
									<draggable v-model="starredPagesMore" :group="{ name: 'pinList' }" class="cursor-move">
										<li
											class="starred-page--more flex items-center cursor-pointer"
											v-for="page in starredPagesMore"
											:key="page.url"
											@click="$router.push(page.url).catch(() => {})"
										>
											<feather-icon svgClasses="h-5 w-5" class="ml-2 mr-1" :icon="page.labelIcon"></feather-icon>
											<span class="px-2 pt-2 pb-1">{{ page.label }}</span>
										</li>
									</draggable>
								</ul>
							</vs-dropdown-menu>
						</vs-dropdown>
					</div>

					<div class="bookmark-container">
						<feather-icon icon="StarIcon" svgClasses="stoke-current text-warning" class="cursor-pointer p-2" @click.stop="showBookmarkPagesDropdown = !showBookmarkPagesDropdown" />
						<div v-click-outside="outside" class="absolute bookmark-list w-1/3 xl:w-1/4 mt-4" v-if="showBookmarkPagesDropdown">
							<vx-auto-suggest
								:autoFocus="true"
								:data="navbarSearchAndPinList"
								@selected="selected"
								@actionClicked="actionClicked"
								inputClassses="w-full"
								show-action
								show-pinned
								background-overlay
							></vx-auto-suggest>
						</div>
					</div>
				</template>

				<router-link tag="div" to="/" class="vx-logo cursor-pointer mx-auto flex items-center">
					<img :src="logo" alt="logo" class="w-10 mr-4" v-if="logo" />
					<span class="vx-logo-text">{{ $t("BeOnTime") }}</span>
				</router-link>

				<!-- SEARCHBAR -->
				<div class="search-full-container w-full h-full absolute left-0" :class="{ flex: showFullSearch }" v-show="showFullSearch">
					<vx-auto-suggest
						class="w-full"
						inputClassses="w-full vs-input-no-border vs-input-no-shdow-focus"
						:autoFocus="showFullSearch"
						:data="navbarSearchAndPinList"
						icon="SearchIcon"
						placeholder="Search..."
						ref="navbarSearch"
						@closeSearchbar="showFullSearch = false"
						@selected="selected"
						background-overlay
					/>
					<div class="absolute right-0 h-full z-50">
						<feather-icon icon="XIcon" class="px-4 cursor-pointer h-full close-search-icon" @click="showFullSearch = false"></feather-icon>
					</div>
				</div>
				<!-- <feather-icon icon="SearchIcon" @click="showFullSearch = true"
                      class="cursor-pointer navbar-fuzzy-search ml-4"></feather-icon> -->

				<!-- CART DROPDOWN -->

				<!-- NOTIFICATIONS -->
				<vs-dropdown vs-custom-content vs-trigger-click class="cursor-pointer">
					<feather-icon icon="BellIcon" class="cursor-pointer mt-1 sm:mr-6 mr-2" :badge="unreadNotifications.length"></feather-icon>
					<vs-dropdown-menu class="notification-dropdown dropdown-custom vx-navbar-dropdown">
						<div class="notification-top text-center p-5 bg-primary text-white">
							<h3 class="text-white">{{ unreadNotifications.length }} New</h3>
							<p class="opacity-75">App Notifications</p>
						</div>

						<PerfectScrollbar ref="mainSidebarPs" class="scroll-area--nofications-dropdown p-0 mb-10" :settings="settings">
							<ul class="bordered-items">
								<li v-for="ntf in unreadNotifications" :key="ntf.index" class="flex justify-between px-4 py-4 notification cursor-pointer">
									<div class="flex items-start">
										<feather-icon :icon="ntf.icon" :svgClasses="[`text-${ntf.category}`, 'stroke-current mr-1 h-6 w-6']"></feather-icon>
										<div class="mx-2">
											<span class="font-medium block notification-title" :class="[`text-${ntf.category}`]">{{ ntf.title }}</span>
											<small>{{ ntf.msg }}</small>
										</div>
									</div>
									<small class="mt-1 whitespace-no-wrap">{{ elapsedTime(ntf.time) }}</small>
								</li>
							</ul>
						</PerfectScrollbar>
						<div
							class="
								checkout-footer
								fixed
								bottom-0
								rounded-b-lg
								text-primary
								w-full
								p-2
								font-semibold
								text-center
								border
								border-b-0
								border-l-0
								border-r-0
								border-solid
								d-theme-border-grey-light
								cursor-pointer"
						>
							<span>View All Notifications</span>
						</div>
					</vs-dropdown-menu>
				</vs-dropdown>

				<!-- USER META -->
				<div class="the-navbar__user-meta flex items-center" v-if="activeUserInfo.first_name">
					<div class="text-right leading-tight hidden sm:block">
						<p class="font-semibold">{{ user_displayName }}</p>
						<small>Available</small>
					</div>
					<vs-dropdown vs-custom-content vs-trigger-click class="cursor-pointer" ref="menuRef">
						<div class="con-img ml-3">
							<img v-if="activeUserImg" key="onlineImg" :src="activeUserImg" alt="user-img" width="40" height="40" class="rounded-full shadow-md cursor-pointer block" />
						</div>
						<vs-dropdown-menu class="vx-navbar-dropdown">
							<ul style="min-width: 9rem">
								<li class="flex py-2 px-4 cursor-pointer hover:bg-primary hover:text-white" @click="handleClickMenuWithCallback(() => $router.push('/pages/profile').catch(() => {}))">
									<feather-icon icon="UserIcon" svgClasses="w-4 h-4" />
									<span class="ml-2">{{ $t("Profile") }}</span>
								</li>
								<!-- <li class="flex py-2 px-4 cursor-pointer hover:bg-primary hover:text-white" @click="$router.push('/apps/email').catch(() => {})">
									<feather-icon icon="MailIcon" svgClasses="w-4 h-4" />
									<span class="ml-2">Inbox</span>
								</li>
								<li class="flex py-2 px-4 cursor-pointer hover:bg-primary hover:text-white" @click="$router.push('/apps/todo').catch(() => {})">
									<feather-icon icon="CheckSquareIcon" svgClasses="w-4 h-4" />
									<span class="ml-2">Tasks</span>
								</li>
								<li class="flex py-2 px-4 cursor-pointer hover:bg-primary hover:text-white" @click="$router.push('/apps/chat').catch(() => {})">
									<feather-icon icon="MessageSquareIcon" svgClasses="w-4 h-4" />
									<span class="ml-2">Chat</span>
								</li> -->

								<vs-divider class="m-1"></vs-divider>

								<li class="flex py-2 px-4 cursor-pointer hover:bg-primary hover:text-white" @click="logout">
									<feather-icon icon="LogOutIcon" svgClasses="w-4 h-4" />
									<span class="ml-2">Logout</span>
								</li>
							</ul>
						</vs-dropdown-menu>
					</vs-dropdown>
				</div>
			</vs-navbar>
		</div>
	</div>
</template>

<script>
import VxAutoSuggest from "@/components/vx-auto-suggest/VxAutoSuggest.vue";
import draggable from "vuedraggable";

export default {
	name: "the-navbar",
	props: {
		logo: { type: String },
		navbarType: {
			type: String,
			required: true
		}
	},
	data() {
		return {
			navbarSearchAndPinList: this.$store.state.navbarSearchAndPinList,
			searchQuery: "",
			showFullSearch: false,
			unreadNotifications: [
				{
					index: 0,
					title: "New Message",
					msg: "Are your going to meet me tonight?",
					icon: "MessageSquareIcon",
					time: this.randomDate({ sec: 10 }),
					category: "primary"
				},
				{
					index: 1,
					title: "New Order Recieved",
					msg: "You got new order of goods.",
					icon: "PackageIcon",
					time: this.randomDate({ sec: 40 }),
					category: "success"
				},
				{
					index: 2,
					title: "Server Limit Reached!",
					msg: "Server have 99% CPU usage.",
					icon: "AlertOctagonIcon",
					time: this.randomDate({ min: 1 }),
					category: "danger"
				},
				{
					index: 3,
					title: "New Mail From Peter",
					msg: "Cake sesame snaps cupcake",
					icon: "MailIcon",
					time: this.randomDate({ min: 6 }),
					category: "primary"
				},
				{
					index: 4,
					title: "Bruce's Party",
					msg: "Chocolate cake oat cake tiramisu",
					icon: "CalendarIcon",
					time: this.randomDate({ hr: 2 }),
					category: "warning"
				}
			],
			settings: {
				// perfectscrollbar settings
				maxScrollbarLength: 60,
				wheelSpeed: 0.6
			},
			autoFocusSearch: false,
			showBookmarkPagesDropdown: false
		};
	},
	watch: {
		$route() {
			if (this.showBookmarkPagesDropdown) this.showBookmarkPagesDropdown = false;
		}
	},
	computed: {
		navbarColor() {
			let color = "#fff";
			if (this.navbarType === "sticky") color = "#f7f7f7";
			else if (this.navbarType === "static") {
				if (this.scrollY < 50) {
					color = "#f7f7f7";
				}
			}

			this.isThemedark === "dark" ? (color === "#fff" ? (color = "#10163a") : (color = "#262c49")) : null;

			return color;
		},
		isThemedark() {
			return this.$store.state.theme;
		},
		i18n_locale_img() {
			return require(`@/assets/images/flags/${this.$i18n.locale}.png`);
		},
		navbarStyle() {
			let style = {};

			if (this.navbarType === "static") {
				style.transition = "all .25s ease-in-out";

				// if(this.scrollY < 50) {
				//   style.background = "#262c49"
				// }
			}

			return style;
		},
		navbarClasses() {
			return this.scrollY > 5 && this.navbarType === "static" ? null : "d-theme-dark-light-bg shadow-none";
		},
		scrollY() {
			return this.$store.state.scrollY;
		},

		// HELPER
		verticalNavMenuWidth() {
			return this.$store.state.verticalNavMenuWidth;
		},
		windowWidth() {
			return this.$store.state.windowWidth;
		},

		// I18N
		getCurrentLocaleData() {
			const locale = this.$i18n.locale;
			if (locale === "en") return { flag: "us", lang: "English" };
			else if (locale === "vi") return { flag: "vi", lang: "Vietnamese" };
		},

		// BOOKMARK & SEARCH
		data() {
			return this.$store.state.navbarSearchAndPinList;
		},
		starredPages() {
			return this.$store.state.starredPages;
		},
		starredPagesLimited: {
			get() {
				return this.starredPages.slice(0, 10);
			},
			set(list) {
				this.$store.dispatch("arrangeStarredPagesLimited", list);
			}
		},
		starredPagesMore: {
			get() {
				return this.starredPages.slice(10);
			},
			set(list) {
				this.$store.dispatch("arrangeStarredPagesMore", list);
			}
		},

		// CART DROPDOWN
		cartItems() {
			return this.$store.state.eCommerce.cartItems.slice().reverse();
		},

		// PROFILE
		activeUserInfo() {
			return this.$store.state.AppActiveUser;
		},
		user_displayName() {
			return this.activeUserInfo.first_name;
		},
		activeUserImg() {
			return this.$store.state.AppActiveUser.role.avatar;
		}
	},
	methods: {
		updateLocale(locale) {
			this.$i18n.locale = locale;
		},
		selected(item) {
			this.$router.push(item.url).catch(() => {});
			this.showFullSearch = false;
		},
		actionClicked(item) {
			// e.stopPropogation();
			this.$store.dispatch("updateStarredPage", { index: item.index, val: !item.highlightAction });
		},
		showNavbarSearch() {
			this.showFullSearch = true;
		},
		showSearchbar() {
			this.showFullSearch = true;
		},
		elapsedTime(startTime) {
			let x = new Date(startTime);
			let now = new Date();
			var timeDiff = now - x;
			timeDiff /= 1000;

			var seconds = Math.round(timeDiff);
			timeDiff = Math.floor(timeDiff / 60);

			var minutes = Math.round(timeDiff % 60);
			timeDiff = Math.floor(timeDiff / 60);

			var hours = Math.round(timeDiff % 24);
			timeDiff = Math.floor(timeDiff / 24);

			var days = Math.round(timeDiff % 365);
			timeDiff = Math.floor(timeDiff / 365);

			var years = timeDiff;

			if (years > 0) {
				return years + (years > 1 ? " Years " : " Year ") + "ago";
			} else if (days > 0) {
				return days + (days > 1 ? " Days " : " Day ") + "ago";
			} else if (hours > 0) {
				return hours + (hours > 1 ? " Hrs " : " Hour ") + "ago";
			} else if (minutes > 0) {
				return minutes + (minutes > 1 ? " Mins " : " Min ") + "ago";
			} else if (seconds > 0) {
				return seconds + (seconds > 1 ? " sec ago" : "just now");
			}

			return "Just Now";
		},
		async logout() {
			await this.$router.push("/").catch(() => {});
			// If JWT login
			if (localStorage.getItem("accessToken")) {
				localStorage.removeItem("accessToken");
				this.$router.push("/pages/login").catch(() => {});
			}

			// Change role on logout. Same value as initialRole of acj.js
			this.$acl.change("guest");
			localStorage.removeItem("userInfo");

			// This is just for demo Purpose. If user clicks on logout -> redirect
			this.$router.push("/pages/login").catch(() => {});
		},
		outside: function() {
			this.showBookmarkPagesDropdown = false;
		},

		// CART DROPDOWN
		removeItemFromCart(item) {
			this.$store.dispatch("eCommerce/toggleItemInCart", item);
		},

		// Method for creating dummy notification time
		randomDate({ hr, min, sec }) {
			let date = new Date();

			if (hr) date.setHours(date.getHours() - hr);
			if (min) date.setMinutes(date.getMinutes() - min);
			if (sec) date.setSeconds(date.getSeconds() - sec);

			return date;
		},
		handleClickMenuWithCallback(cb) {
			this.$refs.menuRef.$el.click();
			if (typeof cb !== "undefined" && typeof cb === "function") {
				cb();
			}
		}
	},
	directives: {
		"click-outside": {
			bind: function(el, binding) {
				const bubble = binding.modifiers.bubble;
				const handler = e => {
					if (bubble || (!el.contains(e.target) && el !== e.target)) {
						binding.value(e);
					}
				};
				el.__vueClickOutside__ = handler;
				document.addEventListener("click", handler);
			},

			unbind: function(el) {
				document.removeEventListener("click", el.__vueClickOutside__);
				el.__vueClickOutside__ = null;
			}
		}
	},
	components: {
		VxAutoSuggest,
		draggable
	}
};
</script>
